import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Landingheader from './Landingheader';
import Footer from '../Footer';
import axios from 'axios';
import Cookies from 'js-cookie';

export default function UserProfile() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    customer_image: '',
    profileImageFile: null
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const navigate = useNavigate();

  const getAccount = async () => {
    try {
      const user_token = Cookies.get('user_token');
      if (!user_token) return;

      const response = await axios.post('https://land-survey-main-server.onrender.com/getAccount', { user_token });

      if (response?.data && response.data.status === true) {
        const account = response.data.account;
        setFormData({
          name: account.customer_name || '',
          email: account.customer_email || '',
          phone: account.customer_phonenumber || '',
          password: '', 
          customer_image: account.customer_image || '',
          profileImageFile: null
        });
        
        // Handle database image checking conditions
        if (account.customer_image) {
          setPreviewImage(`https://land-survey-main-server.onrender.com/clients_images/${account.customer_image}`);
        } else {
          setPreviewImage(''); // Fallback to placeholder if profile image is null
        }
      } else {
        setError(response.data.message || "Failed to load profile.");
      }
    } catch (err) {
      console.error("get account error:", err);
      setError("An error occurred while fetching your data.");
    }
  };

  useEffect(() => {
    const user_token = Cookies.get('user_token');
    if (!user_token) {
      navigate('/login');
    } else {
      getAccount();
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, type, files, value } = e.target;

    // Ensure state references check matching HTML component names cleanly
    if (type === "file" || name === "profileImage") {
      if (files && files[0]) {
        const file = files[0];
        setFormData(prev => ({ ...prev, profileImageFile: file }));
        setPreviewImage(URL.createObjectURL(file)); 
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      const token = Cookies.get('user_token');
      if (!token) {
        setError('Your session has expired. Please log in again.');
        return;
      }

      const dataToSend = new FormData();
      dataToSend.append('name', formData.name);
      dataToSend.append('phone', formData.phone);
      dataToSend.append('password', formData.password);
      dataToSend.append('token', token);
      
      // Append file only if the client selected a new image
      if (formData.profileImageFile) {
        dataToSend.append('image', formData.profileImageFile);
      }

      const response = await axios.put('https://land-survey-main-server.onrender.com/updateClientProfile', dataToSend, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (response.data.status === true) {
        setSuccess("Profile settings successfully updated!");
        setTimeout(() => {
          navigate("/Landing");
        }, 2000);
      } else {
        // Maps express-validator arrays correctly into displayable output text
        if (response.data.errors && Array.isArray(response.data.errors)) {
          setError(response.data.errors.map(err => err.msg).join(' '));
        } else {
          setError(response.data.message || "Update failed.");
        }
      }
    } catch (err) {
      console.error('Updating client profile error:', err);
      // Handles fallback friendly messages when an unexpected error triggers (e.g. Server down)
      const friendlyMessage = err.response?.data?.message || 'Our system is experiencing connectivity problems. Please try again soon.';
      setError(friendlyMessage);
    }
  };

  return (
    <>
      <Landingheader />
      <div className="max-w-xl p-6 mx-auto my-10 font-sans bg-white border border-gray-100 shadow-xl rounded-2xl sm:p-8">
        <div className="mb-8 text-center sm:text-left">
          <h2 className="text-2xl font-bold text-gray-800">Account Settings</h2>
          <p className="mt-1 text-sm text-gray-500">Manage and change your contact profile details below.</p>
        </div>

        {error && <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-xl">{error}</div>}
        {success && <div className="p-3 mb-4 text-sm text-green-700 bg-green-100 rounded-xl">{success}</div>}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col items-center gap-4 p-4 border sm:flex-row bg-slate-50 rounded-xl border-slate-100">
            <img 
              src={previewImage || 'https://via.placeholder.com/150?text=No+Image'} 
              className="object-cover w-20 h-20 border-4 border-white rounded-full shadow-md" 
              alt="Avatar preview" 
            />
            <div className="text-center sm:text-left">
              <label className="block text-sm font-semibold text-indigo-600 cursor-pointer hover:text-indigo-500">
                Change Profile Image
                <input type="file" className="hidden" name="profileImage" accept="image/*" onChange={handleChange} />
              </label>
              <p className="mt-1 text-xs text-gray-400">JPG, JPEG or PNG. Max size 2MB.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">Full Name</label>
              <input 
                type="text" 
                name="name"
                value={formData.name} 
                onChange={handleChange}
                className="w-full px-4 py-2.5 border rounded-xl border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                required
              />
            </div>

            <div>
              <label className="block mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">Email Address</label>
              <input 
                type="email" 
                name="email"
                value={formData.email} 
                className="w-full px-4 py-2.5 border rounded-xl border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed text-sm"
                disabled 
              />
            </div>

            <div>
              <label className="block mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">Phone Number</label>
              <input 
                type="tel" 
                name="phone"
                value={formData.phone} 
                onChange={handleChange}
                className="w-full px-4 py-2.5 border rounded-xl border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
              />
            </div>

            <div>
              <label className="block mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">New Password (leave blank to keep current)</label>
              <input 
                type="password" 
                name="password"
                value={formData.password} 
                onChange={handleChange}
                className="w-full px-4 py-2.5 border rounded-xl border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
              />
            </div>
          </div>

          <div className="flex flex-col-reverse justify-end gap-3 pt-4 border-t border-gray-100 sm:flex-row">
            <button 
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition duration-200 text-center"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 shadow-md shadow-indigo-600/10 active:scale-[0.98] rounded-xl transition duration-200 text-center"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
      <Footer />
    </>
  );
}